import React, { useContext } from "react";
import { DataTable, HighlightMatch, LongTextLabel, Table, ValueOrPlaceholder, VerifiedBy, } from "@sscs/ui";
import { getQueryStatus } from "@sscs/utils";
import { FILE_BROWSER_ROUTES, REPORT_PAGES_ROUTES, } from "../../../../constants";
import { licenseFamilyFiltered } from "../../../../store/licensesSlice";
import { expandedRowPaginationUpdated, expandedRowSortUpdated, expandedRowsUpdated, focusedRowUpdated, selectExpandedRow, selectExpandedRowPagination, selectExpandedRowSort, selectFilter, selectFocusedRow, } from "../../../../store/mlbomSlice";
import { ComponentList, ExpandedRowDetails } from "../../../../ui";
import { ComponentName, ComponentPriorityAndIssuesLink, IssuesList, License, } from "../../../common";
import { ReportContext } from "../../../context";
import { useReportDispatch, useReportSelector } from "../../../hooks";
import { useGetModelCardComponentsQuery } from "../../../services/mlbom.api";
import { parseIdentityLicense } from "../../../utils";
import ModelCard from "../ModelCard";
import AISecurityCard from "./AISecurityCard";
import styles from "./mlModel.module.scss";
const ModelDetails = ({ modelCard, rowIndex, }) => {
    var _a;
    const dispatch = useReportDispatch();
    const { reportId } = useContext(ReportContext);
    const pagination = useReportSelector((state) => selectExpandedRowPagination(state, modelCard.id));
    const sortBy = useReportSelector((state) => selectExpandedRowSort(state, modelCard.id));
    const { data, isLoading, isFetching, isError, error } = useGetModelCardComponentsQuery({
        ui_item_id: modelCard.id,
        ui_id: reportId,
        sort: sortBy,
        pagination,
    });
    const handleSortChange = (newSort) => {
        dispatch(expandedRowSortUpdated({
            id: modelCard.id,
            sort: newSort,
        }));
    };
    const showAiSecurityCard = modelCard.testing.present ||
        (data !== undefined
            ? data.components.some((component) => component.purl !== null &&
                component.purl.startsWith("pkg:huggingface/"))
            : false);
    return (React.createElement(ExpandedRowDetails, null,
        React.createElement(ExpandedRowDetails.Aside, null,
            React.createElement(ModelCard, { modelCard: modelCard }),
            React.createElement(IssuesList, { violations: modelCard.violations, to: REPORT_PAGES_ROUTES.ISSUES_ROUTE })),
        React.createElement(ExpandedRowDetails.Main, null,
            showAiSecurityCard && (React.createElement(AISecurityCard, { modelCard: modelCard, rowIndex: rowIndex })),
            React.createElement(ComponentList.Table, { itemId: "ml-models", rowIndex: rowIndex, title: "Components that contain this machine learning model", matchCount: (_a = data === null || data === void 0 ? void 0 : data.match_count) !== null && _a !== void 0 ? _a : 0, queryStatus: getQueryStatus({
                    isLoading,
                    isFetching,
                    isError,
                    error,
                    data,
                }), totalCount: modelCard.component_count, pagination: pagination, onPaginationChange: (newPagination) => dispatch(expandedRowPaginationUpdated({
                    id: modelCard.id,
                    pagination: newPagination,
                })), columns: ComponentList.columns({
                    sortBy,
                    rowIndex,
                    onSortChange: handleSortChange,
                }) }, data === null || data === void 0 ? void 0 : data.components.map((component, index) => {
                var _a;
                return (React.createElement(Table.Row, { key: (_a = component.id) !== null && _a !== void 0 ? _a : component.path },
                    React.createElement(Table.Cell, null,
                        React.createElement(ComponentList.PriorityAndIssues, { component: component, id: index.toString(), badgeWrapper: (target, children) => (React.createElement(ComponentPriorityAndIssuesLink, { htmlId: index.toString(), componentId: component.id, componentPath: component.path, target: target }, children)) })),
                    React.createElement(Table.Cell, null,
                        React.createElement(ComponentName, { component: component, id: index.toString(), fileBrowserPage: FILE_BROWSER_ROUTES.INFO_ROUTE })),
                    React.createElement(Table.Cell, null,
                        React.createElement(ComponentList.Path, { path: component.path }))));
            })))));
};
const MLModel = ({ modelCard, rowIndex }) => {
    const dispatch = useReportDispatch();
    const filter = useReportSelector(selectFilter);
    const isExpanded = useReportSelector((state) => selectExpandedRow(state, modelCard.id));
    const focusedRow = useReportSelector(selectFocusedRow);
    const parsedLicense = parseIdentityLicense(modelCard.license);
    return (React.createElement(DataTable.ExpandedRow, { id: `mlbom-expand-row-${rowIndex}`, expanded: isExpanded, focused: focusedRow === modelCard.id, onChange: (expanded) => dispatch(expandedRowsUpdated({ id: modelCard.id, expand: expanded })), onClick: () => dispatch(focusedRowUpdated(modelCard.id)) },
        React.createElement(DataTable.ExpandedRow.Summary, null,
            React.createElement(Table.Cell, null, modelCard.publisher ? (React.createElement("div", { className: styles.publisher },
                React.createElement(VerifiedBy, { verified: modelCard.verified }),
                React.createElement("span", null, modelCard.publisher))) : (React.createElement(ValueOrPlaceholder, { value: modelCard.publisher }))),
            React.createElement(Table.Cell, null,
                filter.product_or_type ? (React.createElement(HighlightMatch, { value: modelCard.product, regex: filter.product_or_type })) : (React.createElement(ValueOrPlaceholder, { value: modelCard.product })),
                React.createElement(LongTextLabel, { label: `${modelCard.version} ${modelCard.version.toLowerCase() === "generic" ? " Version" : ""}`, className: styles.version })),
            React.createElement(Table.Cell, null,
                filter.product_or_type ? (React.createElement(HighlightMatch, { value: modelCard.type, regex: filter.product_or_type })) : (React.createElement(ValueOrPlaceholder, { value: modelCard.type, placeholder: "Unknown architecture" })),
                React.createElement("div", { className: styles["inputs-outputs"] },
                    React.createElement("strong", null, "Inputs: "),
                    React.createElement(ValueOrPlaceholder, { value: modelCard.inputs.join(", "), placeholder: "unknown" }),
                    "\u00A0\u2192\u00A0",
                    React.createElement("strong", null, "Outputs: "),
                    React.createElement(ValueOrPlaceholder, { value: modelCard.outputs.join(", "), placeholder: "unknown" }))),
            React.createElement(Table.Cell, null,
                React.createElement(ValueOrPlaceholder, { value: modelCard.task })),
            React.createElement(Table.Cell, null,
                React.createElement(License, { id: `mlbom-license-link-${rowIndex}`, license: modelCard.license, to: REPORT_PAGES_ROUTES.LICENSES_ROUTE, onClick: () => { var _a; return dispatch(licenseFamilyFiltered((_a = parsedLicense === null || parsedLicense === void 0 ? void 0 : parsedLicense.family) !== null && _a !== void 0 ? _a : "")); } })),
            React.createElement(Table.Cell, null, modelCard.component_count.toLocaleString())),
        React.createElement(DataTable.ExpandedRow.Details, null,
            React.createElement(ModelDetails, { modelCard: modelCard, rowIndex: rowIndex }))));
};
export default MLModel;
//# sourceMappingURL=MLModel.js.map